
import { LeakItem } from '@/types/dashboard';
import { filterByDate } from './dashboardDataProcessors';
import { getThirtyDaysAgo, getDaysAgo } from './dateUtils';

/**
 * Process leaks data and return recent items
 */
export function processLeaksData(leaksData: any[]): LeakItem[] {
  // Ensure we're working with an array
  const leaksArray = Array.isArray(leaksData) ? leaksData : [];

  // If no data, return empty array
  if (leaksArray.length === 0) {
    return [];
  }

  // Get the date from 30 days ago for filtering
  const thirtyDaysAgo = getThirtyDaysAgo();

  // Filter leaks to only include those from the last 30 days
  // If there are no recent leaks within 30 days, use all leaks
  let recentLeaks = filterByDate(leaksArray, 'breach_date', thirtyDaysAgo);

  // If we don't have enough recent leaks, just use all leaks sorted by date
  if (recentLeaks.length < 4) {
    recentLeaks = leaksArray;
  }

  // Sort the leaks by date (most recent first)
  const sortedLeaks = [...recentLeaks].sort((a: any, b: any) => {
    const dateA = new Date(a.breach_date).getTime();
    const dateB = new Date(b.breach_date).getTime();
    return dateB - dateA; // Sort by most recent first
  });

  // Return the 4 most recent leak items
  return sortedLeaks.slice(0, 4) as LeakItem[];
}

/**
 * Count the number of leaks that occurred in the last 30 days
 */
/**
 * Count the number of leaks that occurred in the last N days
 */
export function countRecentLeaks(leaksData: any[], days: number = 30): { current: number, previous: number } {
  // Ensure we're working with an array
  const leaksArray = Array.isArray(leaksData) ? leaksData : [];

  // If no data, return 0
  if (leaksArray.length === 0) {
    return { current: 0, previous: 0 };
  }

  // Get the date from N days ago for filtering
  const startDate = getDaysAgo(days);
  const previousStartDate = getDaysAgo(days * 2);

  // Filter leaks to only include those from the last N days
  const recentLeaks = filterByDate(leaksArray, 'breach_date', startDate);

  // Filter for previous period
  const previousLeaks = leaksArray.filter(item => {
    const date = new Date(item.breach_date);
    return date >= previousStartDate && date < startDate;
  });

  // Return the counts
  return {
    current: recentLeaks.length,
    previous: previousLeaks.length
  };
}
